export interface CacheEntry<T> {
  data: T;
  timestamp: number;
  version: string;
}

export class CacheService {
  private static readonly CACHE_VERSION = '1.0.0';
  private static readonly DEFAULT_TTL = 24 * 60 * 60 * 1000; // 24 hours

  /**
   * Set data in cache with optional TTL
   */
  static async setCache<T>(key: string, data: T, ttl?: number): Promise<void> {
    try {
      const cacheEntry: CacheEntry<T> = {
        data,
        timestamp: Date.now(),
        version: this.CACHE_VERSION
      };

      const serializedData = JSON.stringify(cacheEntry);
      localStorage.setItem(key, serializedData);
    } catch (error) {
      console.warn('Failed to cache data:', error);
    }
  }

  /**
   * Get data from cache if valid and not expired
   */
  static async getCache<T>(key: string, ttl?: number): Promise<T | null> {
    try {
      const serializedData = localStorage.getItem(key);

      if (!serializedData) {
        return null;
      }

      const cacheEntry: CacheEntry<T> = JSON.parse(serializedData);

      // Check version compatibility
      if (cacheEntry.version !== this.CACHE_VERSION) {
        await this.removeCache(key);
        return null;
      }

      // Check TTL
      const maxAge = ttl || this.DEFAULT_TTL;
      if (Date.now() - cacheEntry.timestamp > maxAge) {
        await this.removeCache(key);
        return null;
      }

      return cacheEntry.data;
    } catch (error) {
      console.warn('Failed to retrieve cached data:', error);
      return null;
    }
  }

  /**
   * Remove data from cache
   */
  static async removeCache(key: string): Promise<void> {
    try {
      localStorage.removeItem(key);
    } catch (error) {
      console.warn('Failed to remove cached data:', error);
    }
  }

  /**
   * Clear all cache entries
   */
  static async clearAllCache(): Promise<void> {
    try {
      localStorage.clear();
    } catch (error) {
      console.warn('Failed to clear all cache:', error);
    }
  }

  /**
   * Check if data exists in cache (without retrieving it)
   */
  static async hasCache(key: string): Promise<boolean> {
    try {
      return localStorage.getItem(key) !== null;
    } catch (error) {
      return false;
    }
  }

  /**
   * Clean up oversized cache entries
   */
  static async cleanupOversizedCache(): Promise<void> {
    try {
      console.log('Cleaning up oversized cache entries...');
      
      // Remove thumbnail cache entries from localStorage
      const keysToRemove: string[] = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.includes('cache_thumbnail_')) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach(key => localStorage.removeItem(key));
      console.log(`Cleared ${keysToRemove.length} thumbnail cache entries from localStorage`);
    } catch (error) {
      console.warn('Failed to cleanup oversized cache:', error);
    }
  }
}

/**
 * Cache key generators for different data types
 */
export const CacheKeys = {
  recipes: () => 'cache_recipes',
  recipe: (id: string) => `cache_recipe_${id}`,
  mealPlans: (startDate: string, endDate: string) => `cache_meal_plans_${startDate}_${endDate}`,
  mealCompositions: () => 'cache_meal_compositions',
  mealNotes: (startDate: string, endDate: string) => `cache_meal_notes_${startDate}_${endDate}`,
  shoppingList: (familyId: string) => `cache_shopping_list_${familyId}`,  
  userProfile: (userId: string) => `cache_user_profile_${userId}`,
  recipeThumbnail: (imageUrl: string) => `cache_thumbnail_${btoa(imageUrl).replace(/[/+=]/g, '')}`,
} as const;
